import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger } from
"@/shared/components/ui/tooltip";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Info } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Get color intensity based on activity score
const getHeatmapColor = (score, maxScore) => {
  if (score === 0) return 'bg-slate-100 dark:bg-slate-800';

  const intensity = Math.min(score / Math.max(maxScore, 1), 1);

  if (intensity < 0.25) return 'bg-emerald-200 dark:bg-emerald-900';
  if (intensity < 0.5) return 'bg-emerald-400 dark:bg-emerald-700';
  if (intensity < 0.75) return 'bg-emerald-500 dark:bg-emerald-600';
  return 'bg-emerald-600 dark:bg-emerald-500';
};

// Get day name abbreviation
const getDayName = (dayIndex) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[dayIndex];
};

// Get month name
const getMonthName = (monthIndex) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[monthIndex];
};

const ConsistencyHeatmap = ({ workspaceId, channel = null, campaignId = null }) => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(true);
  const [heatmapData, setHeatmapData] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    if (workspaceId) {
      fetchHeatmapData();
    }
  }, [workspaceId, channel, campaignId, year]);

  const fetchHeatmapData = async () => {
    setLoading(true);
    try {
      // Calculate date range for current year view
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      let url = `${API_URL}/api/cns/workspaces/${workspaceId}/heatmap?start_date=${startDate}&end_date=${endDate}`;
      if (channel) url += `&channel=${channel}`;
      if (campaignId) url += `&campaign_id=${campaignId}`;

      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setHeatmapData(data);
      }
    } catch (err) {
      console.error('Failed to fetch heatmap data');
    } finally {
      setLoading(false);
    }
  };

  // Generate grid data for the year
  const generateGridData = () => {
    if (!heatmapData) return [];

    const cellsMap = new Map();
    (heatmapData.cells || []).forEach((cell) => {
      cellsMap.set(cell.date, cell);
    });

    const grid = [];
    const today = new Date();
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year, 11, 31);

    // Adjust start to first Sunday
    const firstDayOfWeek = startOfYear.getDay();
    const adjustedStart = new Date(startOfYear);
    adjustedStart.setDate(adjustedStart.getDate() - firstDayOfWeek);

    let currentDate = new Date(adjustedStart);
    let currentWeek = [];
    let weekIndex = 0;

    while (currentDate <= endOfYear || currentWeek.length > 0) {
      const dateStr = currentDate.toISOString().split('T')[0];
      const isCurrentYear = currentDate.getFullYear() === year;
      const cellData = cellsMap.get(dateStr) || {
        date: dateStr,
        posts_count: 0,
        activity_score: 0
      };

      currentWeek.push({
        ...cellData,
        isCurrentYear,
        isFuture: currentDate > today,
        dayOfWeek: currentDate.getDay(),
        month: currentDate.getMonth(),
        day: currentDate.getDate()
      });

      if (currentWeek.length === 7) {
        grid.push({
          weekIndex,
          days: currentWeek
        });
        currentWeek = [];
        weekIndex++;
      }

      currentDate.setDate(currentDate.getDate() + 1);

      // Safety break
      if (weekIndex > 54) break;
    }

    return grid;
  };

  const gridData = generateGridData();
  const maxScore = Math.max(...(heatmapData?.cells || []).map((c) => c.activity_score || 0), 1);

  // Calculate month labels
  const monthLabels = [];
  let currentMonth = -1;
  gridData.forEach((week, weekIdx) => {
    const firstDay = week.days.find((d) => d.isCurrentYear && d.day <= 7);
    if (firstDay && firstDay.month !== currentMonth) {
      currentMonth = firstDay.month;
      monthLabels.push({ month: currentMonth, weekIndex: weekIdx });
    }
  });

  if (loading) {
    return (
      <Card className="card-elevated">
        <CardContent className="p-6">
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
          </div>
        </CardContent>
      </Card>);

  }

  return (
    <Card className="card-elevated">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-emerald-600" />{t("consistencyHeatmap", "Consistency Heatmap")}

          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setYear(year - 1)}>
              
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium w-12 text-center">{year}</span>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setYear(year + 1)}
              disabled={year >= new Date().getFullYear()}>
              
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="p-4">
        {/* Summary Stats */}
        <div className="flex items-center gap-6 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t("totalPosts", "Total Posts:")}</span>
            <span className="font-semibold text-emerald-600">
              {heatmapData?.summary?.total_posts || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t("activeDays", "Active Days:")}</span>
            <span className="font-semibold">
              {heatmapData?.summary?.active_days || 0}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">{t("consistency", "Consistency:")}</span>
            <span className="font-semibold text-emerald-600">
              {heatmapData?.summary?.consistency_rate || 0}%
            </span>
          </div>
        </div>

        {/* Heatmap Grid */}
        <div className="overflow-x-auto">
          <div className="min-w-[800px]">
            {/* Month Labels */}
            <div className="flex mb-1 pl-8">
              {monthLabels.map(({ month, weekIndex }) =>
              <div
                key={month}
                className="text-xs text-muted-foreground"
                style={{
                  position: 'relative',
                  left: `${weekIndex * 14}px`,
                  marginRight: month === 11 ? 0 : -14
                }}>
                
                  {getMonthName(month)}
                </div>
              )}
            </div>

            {/* Grid */}
            <div className="flex">
              {/* Day Labels */}
              <div className="flex flex-col gap-[2px] pr-2 text-xs text-muted-foreground">
                <div className="h-3"></div>
                <div className="h-3">{t("mon", "Mon")}</div>
                <div className="h-3"></div>
                <div className="h-3">{t("wed", "Wed")}</div>
                <div className="h-3"></div>
                <div className="h-3">{t("fri", "Fri")}</div>
                <div className="h-3"></div>
              </div>

              {/* Weeks */}
              <div className="flex gap-[2px]">
                <TooltipProvider>
                  {gridData.map((week) =>
                  <div key={week.weekIndex} className="flex flex-col gap-[2px]">
                      {week.days.map((day, dayIdx) => {
                      const isActive = day.posts_count > 0 && day.isCurrentYear;
                      const colorClass = day.isCurrentYear && !day.isFuture ?
                      getHeatmapColor(day.activity_score || 0, maxScore) :
                      'bg-slate-50 dark:bg-slate-900';

                      return (
                        <Tooltip key={dayIdx}>
                            <TooltipTrigger asChild>
                              <div
                              className={`w-3 h-3 rounded-sm cursor-pointer transition-all hover:ring-2 hover:ring-emerald-400 hover:ring-offset-1 ${colorClass} ${!day.isCurrentYear ? 'opacity-30' : ''}`}
                              onClick={() => setSelectedCell(day)} />
                            
                            </TooltipTrigger>
                            <TooltipContent side="top" className="text-xs">
                              <div className="font-semibold">{day.date}</div>
                              <div className="text-muted-foreground">
                                {day.posts_count}{t("posts", "posts \xB7")}{Math.round(day.activity_score)}{t("pts", "pts")}
                            </div>
                              {day.total_engagement > 0 &&
                            <div className="text-emerald-600">
                                  {day.total_engagement}{t("engagement", "engagement")}
                            </div>
                            }
                            </TooltipContent>
                          </Tooltip>);

                    })}
                    </div>
                  )}
                </TooltipProvider>
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-end mt-4 gap-2 text-xs text-muted-foreground">
              <span>{t("less", "Less")}</span>
              <div className="flex gap-1">
                <div className="w-3 h-3 rounded-sm bg-slate-100 dark:bg-slate-800" />
                <div className="w-3 h-3 rounded-sm bg-emerald-200 dark:bg-emerald-900" />
                <div className="w-3 h-3 rounded-sm bg-emerald-400 dark:bg-emerald-700" />
                <div className="w-3 h-3 rounded-sm bg-emerald-500 dark:bg-emerald-600" />
                <div className="w-3 h-3 rounded-sm bg-emerald-600 dark:bg-emerald-500" />
              </div>
              <span>{t("more", "More")}</span>
            </div>
          </div>
        </div>

        {/* Selected Day Detail */}
        {selectedCell && selectedCell.isCurrentYear &&
        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{selectedCell.date}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedCell.posts_count}{t("posts", "posts \xB7")}{selectedCell.stories_count || 0}{t("stories", "stories \xB7")}{selectedCell.reels_count || 0}{t("reels", "reels")}
              </p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-emerald-600">
                  {Math.round(selectedCell.activity_score)}{t("points", "points")}
              </p>
                <p className="text-xs text-muted-foreground">
                  {selectedCell.total_engagement || 0}{t("engagement", "engagement")}
              </p>
              </div>
            </div>
          </div>
        }
      </CardContent>
    </Card>);

};

export default ConsistencyHeatmap;